import { forwardRef, type MouseEvent } from 'react';
import Link from 'next/link';
import RestaurantRating from '@discover-components/restaurant/rating';
import { type RestaurantDetails } from '@discover-data/stores/restaurant/gql/getters/restaurantDetails';
import { cn } from '@discover-lib/utils';
import AvailabilityChips, {
  type AvailabilityChipsProps,
} from '@discover-components/availability/section/chips';
import { hasRestaurantReviewSummary } from '@discover-components/restaurant/reviewSummary';
import FavouriteWrapper from '@/components/organisms/Favourites/FavouriteWrapper';
import Experiment from '@/components/analytics/experiments/experiment';
import { PD6665_NEW_USER_DISCOUNT_FLAG } from '@/components/analytics/experiments/PD6665NewUserDiscount';
import getRestaurantHref from '@discover-components/restaurant/getRestaurantHref';
import hasSessionTypeForFilter from '@discover-data/stores/restaurant/utils/discountAvailability/hasSessionTypeForFilter';
import RestaurantCardImage from './image';
import RestaurantCardMediaButtons from './mediaButtons';
import RestaurantCardSubtitleRow from './subtitleRow';
import RestaurantCardTitleRow from './titleRow';
import RestaurantCardRoot from './root';

export type RestaurantCardProps = {
  restaurant: Pick<
    RestaurantDetails,
    | 'id'
    | 'menuTitle'
    | 'rating'
    | 'approvedReviewsCount'
    | 'suburb'
    | 'region'
    | 'tags'
    | 'slug'
    | 'firstTableSessionTypes'
    | 'gallery'
    | 'featuredText'
    | 'isNew'
    | 'regionRanking'
    | 'acceptsAmex'
    | 'images'
    | 'menus'
    | 'reviewSummary'
    | 'reviewSummaryDetails'
  > & {
    id: number;
  };
  imagePriority?: boolean;
  availabilitySections: React.ReactNode[];
  session: SessionType;
  people: number;
  restaurantChips?: AvailabilityChipsProps;
  onRestaurantClick?: () => void;
  onOpenReviewSummary?: () => void;
} & React.ComponentPropsWithoutRef<'div'>;

const RestaurantCard = forwardRef<HTMLDivElement, RestaurantCardProps>(
  (
    {
      restaurant,
      imagePriority = false,
      availabilitySections,
      session,
      people,
      restaurantChips,
      onRestaurantClick,
      onOpenReviewSummary,
      ...props
    },
    forwardedRef
  ) => {
    const {
      menuTitle,
      rating,
      approvedReviewsCount,
      suburb,
      region,
      gallery,
      tags,
      slug,
      firstTableSessionTypes,
      featuredText,
      isNew,
      regionRanking,
      acceptsAmex,
      images,
      menus,
    } = restaurant;

    const newBadge = isNew ? 'New Addition' : undefined;
    const ratingBadge = regionRanking ? `Top Rated #${regionRanking}` : undefined;
    const badge = featuredText || newBadge || ratingBadge;

    const showReviewSummaryModal = hasRestaurantReviewSummary(restaurant);
    const hasTreatmentChips = !!restaurantChips?.fee || !!restaurantChips?.discountLabel;
    const hasAvailabilityChips =
      hasTreatmentChips || (restaurantChips?.points !== undefined && +restaurantChips.points > 0);

    // A session the restaurant doesn't offer would leave the restaurant page's store session out of sync with its booking widget.
    const linkedSession = hasSessionTypeForFilter(firstTableSessionTypes, session)
      ? session
      : undefined;
    const restaurantHref = getRestaurantHref(slug, people, undefined, linkedSession);
    const reviewsHref = getRestaurantHref(slug, people, 'reviews');

    const handleReviewsClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (showReviewSummaryModal && onOpenReviewSummary) {
        event.preventDefault();
        onOpenReviewSummary();
        return;
      }

      onRestaurantClick?.();
    };

    const ratingLink = !!rating && !!approvedReviewsCount && (
      <Link
        href={reviewsHref}
        onClick={handleReviewsClick}
        className="no-underline hover:underline">
        <RestaurantRating
          className="text-[14px] font-medium leading-[20px] text-ds-gray-light-700"
          rating={rating}
          count={approvedReviewsCount}
          layout="compact"
        />
      </Link>
    );

    return (
      <RestaurantCardRoot ref={forwardedRef} {...props}>
        <div
          className={cn(
            'self-start lg:h-full',
            availabilitySections.length === 1 && 'lg:row-span-2',
            availabilitySections.length === 2 && 'lg:row-span-3',
            availabilitySections.length === 3 && 'lg:row-span-4'
          )}>
          <FavouriteWrapper restaurant={restaurant}>
            <RestaurantCardImage
              slug={restaurantHref}
              image={gallery}
              images={images?.map((image) => image.url)}
              title={menuTitle}
              badge={badge}
              priority={imagePriority}
              onRestaurantClick={onRestaurantClick}
            />
          </FavouriteWrapper>
        </div>
        <Experiment
          flag={PD6665_NEW_USER_DISCOUNT_FLAG}
          control={
            <>
              <div className="flex flex-col gap-1.5 self-stretch sm:gap-2">
                <RestaurantCardTitleRow
                  slug={restaurantHref}
                  reviewsHref={reviewsHref}
                  menuTitle={menuTitle}
                  rating={rating}
                  approvedReviewsCount={approvedReviewsCount}
                  onRestaurantClick={onRestaurantClick}
                  onReviewsClick={handleReviewsClick}
                />
                <RestaurantCardSubtitleRow
                  acceptsAmex={acceptsAmex || false}
                  suburb={suburb}
                  region={region}
                  tags={tags}
                  session={session}
                />
                {!!rating && !!approvedReviewsCount && (
                  <Link href={reviewsHref} onClick={handleReviewsClick} className="md:hidden">
                    <RestaurantRating
                      className="text-[14px] leading-[20px]"
                      rating={rating}
                      count={approvedReviewsCount}
                    />
                  </Link>
                )}
                <div className="flex flex-col flex-wrap gap-2.5 empty:hidden md:mt-2">
                  <RestaurantCardMediaButtons images={images} menus={menus} />
                  {hasAvailabilityChips && restaurantChips && (
                    <AvailabilityChips {...restaurantChips} className="hidden sm:flex" />
                  )}
                </div>
              </div>
              {hasAvailabilityChips && restaurantChips && (
                <div className="relative col-span-2 flex w-full sm:hidden">
                  <AvailabilityChips {...restaurantChips} className="w-full" />
                </div>
              )}
            </>
          }
          treatment={
            <>
              <div className="flex flex-col gap-1.5 self-stretch sm:gap-2">
                <RestaurantCardTitleRow
                  slug={restaurantHref}
                  reviewsHref={reviewsHref}
                  menuTitle={menuTitle}
                  rating={rating}
                  approvedReviewsCount={approvedReviewsCount}
                  hideRating
                  onRestaurantClick={onRestaurantClick}
                  onReviewsClick={handleReviewsClick}
                />
                <RestaurantCardSubtitleRow
                  acceptsAmex={acceptsAmex || false}
                  suburb={suburb}
                  region={region}
                  tags={tags}
                  session={session}
                  points={restaurantChips?.points}
                  mobileRating={ratingLink || undefined}
                />
                {!!rating && !!approvedReviewsCount && (
                  <Link
                    href={reviewsHref}
                    onClick={handleReviewsClick}
                    className="hidden no-underline hover:underline md:inline">
                    <RestaurantRating
                      className="leading-tight"
                      rating={rating}
                      count={approvedReviewsCount}
                    />
                  </Link>
                )}
                <div className="flex flex-col flex-wrap gap-2.5 empty:hidden sm:w-fit md:mt-2">
                  <RestaurantCardMediaButtons images={images} menus={menus} stretch />
                  {hasTreatmentChips && restaurantChips && (
                    <AvailabilityChips
                      {...restaurantChips}
                      points={undefined}
                      className="hidden sm:flex"
                    />
                  )}
                </div>
              </div>
              {hasTreatmentChips && restaurantChips && (
                <div className="relative col-span-2 flex w-full sm:hidden">
                  <AvailabilityChips {...restaurantChips} points={undefined} className="w-full" />
                </div>
              )}
            </>
          }
        />
        {availabilitySections.map((section, index) => (
          <div key={index} className="col-span-2 lg:col-span-1">
            {section}
          </div>
        ))}
      </RestaurantCardRoot>
    );
  }
);

RestaurantCard.displayName = 'RestaurantCard';

export default RestaurantCard;
